import { Modal } from 'components/Modal';
import { IosCloseButton } from 'components/iOSCloseButton';
import { BFForm, BFFormInput } from 'form/BFForm';
import { useMobileLayout } from 'hooks/useMobileLayout';
import { useUser } from 'hooks/useUser';
import React from 'react';
import { storeDispatch } from 'state/Store';
import { closeModal } from 'state/actions';
import styled from 'styled-components';
import { generateClassName } from 'utils';
import {
  skipSegmentationSurvey,
  submitSegmentationSurvey
} from 'utils/FirebaseUtils';
import { trackEvent } from 'utils/Tracker';

enum SurveyStep {
  Occupation = 'occupation',
  Industry = 'industry',
  Usecase = 'usecase',
  Reference = 'reference',
  AudienceSize = 'audienceSize'
}

interface Step {
  id: SurveyStep;
  label: string;
  options: string[];
  allowOther: boolean;
}

const steps: Step[] = [
  {
    id: SurveyStep.Occupation,
    label: 'What is your occupation?',
    options: [
      'Event organizer',
      'Musician/Producer',
      'Small business owner',
      'Marketer',
      'Designer'
    ],
    allowOther: true
  },
  {
    id: SurveyStep.Industry,
    label: 'In which industry do you operate?',
    options: [
      'Entertainment',
      'Marketing & Advertising',
      'Retail',
      'Technology',
      'Hospitality',
      'Non-profit'
    ],
    allowOther: true
  },
  {
    id: SurveyStep.Usecase,
    label: "What's your main use for Beatflyer?",
    options: [
      'Promoting events',
      'Sharing music productions',
      'Marketing products/services',
      'Showcasing design projects',
      'Podcast promotions'
    ],
    allowOther: true
  },
  {
    id: SurveyStep.Reference,
    label: 'How did you hear about it?',
    options: [
      'Instagram',
      'Other social media',
      'From a friend',
      'Online ad',
      'Search engine',
      'Blog/Article'
    ],
    allowOther: true
  },
  {
    id: SurveyStep.AudienceSize,
    label: 'How large is your audience?',
    options: [
      'Less than 500',
      '501 - 5,000',
      '5,001 - 20,000',
      'More than 20,000',
      'Prefer not to say'
    ],
    allowOther: false
  }
];

const OptionButton = styled.button`
  height: 56px;
  font-size: 16px;
  font-weight: 700;
  color: black;
  padding: 0 16px;
  display: block;
  margin-bottom: 8px;
  width: 100%;
  text-align: left;
  border: solid 1px rgba(var(--primary-fg-rgb), 0.1);
  background-color: rgba(var(--primary-fg-rgb), 0.03);
  transition: background 0.25s cubic-bezier(0.215, 0.61, 0.355, 1),
    border 0.25s cubic-bezier(0.215, 0.61, 0.355, 1);
  border-radius: 6px;

  &:hover {
    background-color: var(--page-bg-darker-80);
    border: solid 1px rgba(var(--primary-fg-rgb), 0.2);
  }

  &:active {
    border: solid 1px var(--accent-color);
  }
`;

interface StepViewProps {
  step: Step;
  onDone: (stepId: SurveyStep, value: string) => void;
}

const StepView: React.FC<StepViewProps> = p => {
  const { step, onDone } = p;
  const [otherValue, setOtherValue] = React.useState('');
  return (
    <BFForm onSubmit={e => e.preventDefault()}>
      <div style={{ marginBottom: 12, textAlign: 'center' }}>{step.label}</div>
      <div>
        {step.options.map(item => (
          <OptionButton
            onClick={() => onDone(step.id, item)}
            key={item}
            type="button"
          >
            {item}
          </OptionButton>
        ))}
      </div>

      {step.allowOther && (
        <>
          <BFFormInput
            type="text"
            name="other"
            value={otherValue}
            placeholder="Other"
            onChange={v => setOtherValue(v)}
            tall={true}
          />
          <div style={{ textAlign: 'right' }}>
            <button
              type="submit"
              onClick={() => onDone(step.id, otherValue.trim())}
              className="large primary"
              disabled={otherValue.trim().length === 0}
            >
              Next
            </button>
          </div>
        </>
      )}
    </BFForm>
  );
};

interface Props {
  animated: boolean;
}

export const SegmentationSurvey: React.FC<Props> = p => {
  const { animated } = p;
  const [stepIndex, setStepIndex] = React.useState(0);
  const values = React.useRef<Record<string, string>>({});
  const isWebApp = useMobileLayout();
  const user = useUser();
  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  const onClose = React.useCallback(() => {
    if (user && !hasSubmitted) {
      void skipSegmentationSurvey(user.uid);
      trackEvent('Segmentation survey skipped');
    }
    storeDispatch(closeModal());
  }, [user, hasSubmitted]);

  const onDone = React.useCallback(
    (stepId: SurveyStep, value: string) => {
      values.current[stepId] = value;
      if (stepIndex < steps.length - 1) {
        setStepIndex(stepIndex + 1);
      } else {
        if (user) {
          void submitSegmentationSurvey(user.uid, values.current);
          trackEvent('Segmentation survey completed', values.current);
        }
        setHasSubmitted(true);
      }
    },
    [values, stepIndex, user]
  );

  const step = steps[stepIndex];

  React.useEffect(() => {
    trackEvent('Segmentation survey started');
  }, []);

  return (
    <Modal fadeIn={animated}>
      <div
        className={generateClassName('overlay', {
          opaque: true,
          fullWidth: true
          // fullHeight: true
        })}
        // style={{ backgroundColor: '#F0F0F0' }}
      >
        <div className="overlay-content">
          {hasSubmitted ? (
            <>
              <h1 style={{ marginBottom: 40 }}>
                Thanks for helping improving the product! 🙌
              </h1>
              <div style={{ textAlign: 'center' }}>
                <button
                  type="submit"
                  onClick={onClose}
                  className="large secondary"
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <>
              <h1 style={{ marginBottom: 40 }}>
                Tell us about you to help improve the product? 🙏
              </h1>
              <StepView step={step} key={step.id} onDone={onDone} />
            </>
          )}
        </div>

        {isWebApp ? (
          <IosCloseButton onClick={onClose} />
        ) : (
          <button className="tertiary close close--figma" onClick={onClose} />
        )}
      </div>
    </Modal>
  );
};



// WEBPACK FOOTER //
// ./src/modals/SegmentationSurvey.tsx